import { useEffect, useState } from "react";

/**
 * The height that makes an element reach the bottom of the window.
 *
 * Replaces `height: window.innerHeight - 170` in the three tables. That
 * constant was measured by hand against one particular layout, so it was wrong
 * the moment anything above the table changed height — the view toggle added a
 * row and pushed the page into a second scrollbar, and the maintenance banner
 * does the same whenever the site is switched off. Measuring the element's own
 * top edge cannot drift like that: whatever ends up above it, the table stops
 * exactly at the bottom of the window.
 *
 * Returns null until the first measurement, so the caller can paint something
 * sensible on the very first frame instead of flashing at `minHeight`.
 *
 * @param ref        ref on the element to size
 * @param bottomGap  px to leave below it (its own bottom margin)
 * @param minHeight  never return less than this
 */
export default function useFillHeight(ref, bottomGap, minHeight) {
  const [height, setHeight] = useState(null);

  useEffect(function () {
    var frame = null;

    function measure() {
      const node = ref.current;
      if (!node) return;
      // getBoundingClientRect().top is relative to the viewport, which is
      // exactly what we want: the space left below it is innerHeight - top.
      var next = Math.round(
        window.innerHeight - node.getBoundingClientRect().top - (bottomGap || 0)
      );
      if (minHeight && next < minHeight) next = minHeight;
      // Bail on an unchanged value so this never becomes a render loop.
      setHeight(function (current) {
        return current === next ? current : next;
      });
    }

    function schedule() {
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(measure);
    }

    measure();
    window.addEventListener("resize", schedule);

    // The banner above the table appears after its own database read, and the
    // filter bar rewraps as the window narrows — both move our top edge without
    // a resize event.
    var observer = null;
    if (typeof window.ResizeObserver !== "undefined") {
      observer = new window.ResizeObserver(schedule);
      observer.observe(document.body);
    }

    return function () {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", schedule);
      if (observer) observer.disconnect();
    };
  }, [ref, bottomGap, minHeight]);

  return height;
}
